/**
 * Maps GraphQL `extensions.code` values to user-facing copy. Lives in a
 * service so any new error code is added in one place and every component
 * surfaces the same message.
 */
import { Injectable } from '@angular/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

export interface MapsErrorView {
    code: string;
    message: string;
}

/**
 * Client-raised error carrying a MAPS error code — used by the async search
 * poll loop, where a FAILED job arrives as data (not a thrown GraphQL error)
 * and the overall poll cap fires locally. FromApollo() maps the code through
 * the same MESSAGE_BY_CODE table as server-thrown errors.
 */
export class MapsClientError extends Error {
    constructor(public readonly Code: string, message?: string) {
        super(message || Code);
    }
}

const MESSAGE_BY_CODE: Record<string, string> = {
    UNAUTHENTICATED: 'Your session has expired. Please open the magic link again.',
    FORBIDDEN: 'This area is restricted to AGU staff.',
    INVALID_ACCESS_KEY: 'This access link is invalid or has been revoked.',
    JD_TOO_SHORT: 'Please enter a job description between 50 and 50,000 characters.',
    // Live cap comes from the resolver's `extensions.max`; we substitute it in
    // FromApollo() below. Default phrasing stays readable if the field is missing.
    EXPORT_CAP_EXCEEDED: 'You can export at most {max} candidates at a time.',
    SEARCH_NOT_FOUND: 'This search is no longer available.',
    UPLOAD_TYPE_REJECTED: 'Only PDF, DOCX, and TXT files are accepted.',
    UPLOAD_SIZE_EXCEEDED: 'Files must be 10 MB or smaller.',
    UPLOAD_NOT_FOUND: 'One of the uploaded files could not be located.',
    UPLOAD_NOT_READY: 'One of the uploaded files is still processing.',
    UPLOAD_LIMIT_EXCEEDED: 'You can attach at most four files per search.',
    SIMILARITY_UPSTREAM: 'Our similarity service is temporarily unavailable. Please try again shortly.',
    SIMILARITY_TIMEOUT: 'The search took too long. Please try again.',
    SEARCH_REQUEST_NOT_FOUND: 'The search was interrupted on our side. Please run it again.',
    SEARCH_BUSY: 'A search is already running for this session. Please wait for it to finish.',
    INTERNAL_ERROR: 'Something went wrong on our side. Please try again.',
};

@Injectable({ providedIn: 'root' })
export class MapsErrorService {
    FromApollo(err: unknown): MapsErrorView {
        const ge = CombinedGraphQLErrors.is(err) ? err.errors[0] : undefined;
        const code = err instanceof MapsClientError
            ? err.Code
            : (ge?.extensions?.['code'] as string | undefined) ?? 'INTERNAL_ERROR';
        const template = MESSAGE_BY_CODE[code] ?? MESSAGE_BY_CODE['INTERNAL_ERROR']!;
        const max = ge?.extensions?.['max'];
        const message = typeof max === 'number'
            ? template.replace('{max}', String(max))
            : template.replace('{max}', '30');
        return { code, message };
    }
}
